import MuiMenu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import MenuLink from "./MenuLink";
import ROUTES from "../../../../routes/routesModel";
import { useUser } from "../../../../users/providers/UserProvider";
import useHandleUser from "../../../../users/hooks/useHandleUser";
import useCategories from "../../../../cards/hooks/useCategories";
import { useState, useEffect } from "react";
import { Typography } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

type Props = {
  isOpen: boolean;
  anchorEl: HTMLElement;
  onClose: () => void;
};

const Menu: React.FC<Props> = ({ isOpen, anchorEl, onClose }) => {
  const { user } = useUser();
  const { handleLogout } = useHandleUser();
  const { categories } = useCategories();
  const navigate = useNavigate();
  const [subMenuAnchor, setSubMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      console.log("📱 MENU IS OPEN!");
      console.log("Categories in menu:", categories);
    }
  }, [isOpen, categories]);

  const onLogout = () => {
    handleLogout();
    onClose();
  };

  const handleCategoryClick = (
    event: React.MouseEvent<HTMLElement>,
    category: any
  ) => {
    const hasChildren = category.children && category.children.length > 0;

    alert(`Clicked: ${category.name}\nHas Children: ${hasChildren}`);
    console.log("🟢 CATEGORY CLICKED:", category.name);

    if (hasChildren) {
      console.log("🟡 OPENING SUBMENU FOR:", category.name);
      setSubMenuAnchor(event.currentTarget);
      setSelectedCategory(category);
    } else {
      console.log("🟡 NAVIGATING TO:", category.name);
      navigate(`/category/${category.name}`);
      handleCloseAll();
    }
  };

  const handleChildClick = (childName: string) => {
    console.log("🔵 CHILD CLICKED:", childName);
    navigate(`/category/${childName}`);
    handleCloseAll();
  };

  const handleCloseSubMenu = () => {
    console.log("🔴 CLOSING SUBMENU");
    setSubMenuAnchor(null);
    setSelectedCategory(null);
  };

  const handleCloseAll = () => {
    handleCloseSubMenu();
    onClose();
  };

  return (
    <>
      <MuiMenu
        open={isOpen}
        onClose={handleCloseAll}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            minWidth: 250,
            maxHeight: 500,
            overflowY: "auto",
            backgroundColor: "lightblue", // DEBUG: Make menu visible
          },
        }}
      >
        <Box>
          {/* DEBUG HEADER */}
          <Box sx={{ p: 2, backgroundColor: "red", color: "white" }}>
            <Typography variant="h6">DEBUG: THIS IS THE MENU</Typography>
            <Typography variant="body2">
              Categories: {categories?.length || 0}
            </Typography>
          </Box>

          <MenuLink
            label="about"
            navigateTo={""}
            onClick={onClose}
            styles={{ display: { xs: "block", md: "none" } }}
          />

          {/* Categories Section */}
          {categories && categories.length > 0 ? (
            <Box sx={{ py: 1, backgroundColor: "lightyellow" }}>
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  color: "text.secondary",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  display: "block",
                  mb: 1,
                }}
              >
                קטגוריות (Categories found: {categories.length})
              </Typography>
              {categories.map((category, index) => {
                const hasChildren =
                  category.children && category.children.length > 0;

                return (
                  <MenuItem
                    key={category._id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCategoryClick(e, category);
                    }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      backgroundColor: index % 2 === 0 ? "white" : "lightgray", // Zebra stripes
                      "&:hover": {
                        backgroundColor: "rgba(4, 122, 188, 0.5) !important",
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {index + 1}. {category.name}{" "}
                      {hasChildren && "(HAS CHILDREN)"}
                    </Typography>
                    {hasChildren && <ChevronLeft fontSize="small" />}
                  </MenuItem>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ p: 2, backgroundColor: "orange" }}>
              <Typography>NO CATEGORIES FOUND!</Typography>
            </Box>
          )}

          {!user && (
            <>
              <MenuLink
                label="login"
                navigateTo={""}
                onClick={onClose}
                styles={{ display: { xs: "block", md: "none" } }}
              />
              <MenuLink
                label="signup"
                navigateTo={""}
                onClick={onClose}
                styles={{ display: { xs: "block", md: "none" } }}
              />
            </>
          )}
          {user && (
            <>
              <MenuLink
                label="עריכת משתמש"
                navigateTo={`${ROUTES.EDIT_ACCOUNT}/${user._id}`}
                onClick={onClose}
              />
              <MenuItem onClick={onLogout}>יציאה</MenuItem>
            </>
          )}
        </Box>
      </MuiMenu>

      {/* Submenu for children categories */}
      <MuiMenu
        open={Boolean(subMenuAnchor)}
        anchorEl={subMenuAnchor}
        onClose={handleCloseSubMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            border: "5px solid red",
            backgroundColor: "yellow",
          },
        }}
      >
        <Typography sx={{ px: 2, py: 1, fontWeight: 600 }}>
          SUBMENU: {selectedCategory?.name}
        </Typography>
        {selectedCategory?.children?.map((child: any) => (
          <MenuItem
            key={child._id}
            onClick={() => handleChildClick(child.name)}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": {
                backgroundColor: "rgba(4, 122, 188, 0.12)",
              },
            }}
          >
            {child.name}
          </MenuItem>
        ))}
      </MuiMenu>
    </>
  );
};

export default Menu;
